import { GLOBALMESSAGES } from "../constants/messages";

/**
 * An Error whose message was written for the person on the screen rather than for a developer.
 *
 * Bug 79203: useApi put every caught `err.message` straight into the alert, so a plain coding
 * fault surfaced as "s.forEach is not a function" and an axios rejection as "Request failed with
 * status code 500". Neither tells the user anything, and both leak internals. Marking the errors
 * that DO carry user-ready wording is what lets useApi show those verbatim and fall back to a
 * generic sentence for everything else — suppressing the lot would have thrown away
 * apiClient's normalized network/timeout messages and pifWriteResult's PostIF result messages.
 */
export class UserFacingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UserFacingError";
  }
}

/** Axios codes that all mean the same thing: the request never reached the server. */
const NETWORK_ERROR_CODES = ["ERR_NETWORK", "ERR_INTERNET_DISCONNECTED", "ECONNREFUSED"];

/** True while the browser itself reports no connection. */
export const isOffline = (): boolean =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * True when a failure is the connection being down rather than the app being broken.
 *
 * Bug 75680: apiClient normalized its own rejections, but nothing else did — so the first
 * offline failure read as a network problem and the very next one (a floating service call
 * rejecting into window.onunhandledrejection, a component crashing into the ErrorBoundary,
 * or a plain-axios caller that bypasses apiClient) read as "something went wrong". Every
 * error surface classifies through here so they all agree for as long as the connection is down.
 *
 * `isOffline()` counts on its own: while the browser reports no connection there is nothing
 * the user can do about any failure except restore it, so dressing one up as an application
 * fault only sends them to the administrator. The real message and stack are still logged by
 * the caller either way — this narrows what is *shown*, not what is recorded.
 */
export const isNetworkFailure = (error: unknown): boolean => {
  if (isOffline()) {
    return true;
  }

  const candidate = error as {
    code?: string;
    message?: string;
    response?: unknown;
    isAxiosError?: boolean;
  } | null;

  if (!candidate) {
    return false;
  }
  if (candidate.code && NETWORK_ERROR_CODES.includes(candidate.code)) {
    return true;
  }
  // An axios rejection with no response never got an answer from the server.
  if (candidate.isAxiosError === true && !candidate.response) {
    return true;
  }
  // Axios' own default text, plus the sentence apiClient has already normalized to —
  // a rejection can reach a global handler after passing through that interceptor.
  return (
    candidate.message === "Network Error" || candidate.message === GLOBALMESSAGES.NETWORK_ERROR
  );
};

/**
 * The sentence to put in front of the user for a caught error.
 *
 * Only ever call this for display. The real message and stack still go to Portal_ErrorLog and
 * App Insights through logHandledError / logPortalErrorGlobal — this narrows what is *shown*,
 * not what is recorded.
 */
export const toUserMessage = (error: unknown): string => {
  // Checked before UserFacingError so a message minted while the connection was still up
  // (a timeout, a server-side result string) doesn't outlive the connection it described.
  if (isNetworkFailure(error)) {
    return GLOBALMESSAGES.NETWORK_ERROR;
  }
  return error instanceof UserFacingError ? error.message : GLOBALMESSAGES.GLOBAL_ERROR;
};
